import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AlertCircle, ArrowLeft, Bot, Play, Square, X, ListChecks } from 'lucide-react'; 
import { useVendorBooking, useUpdateBookingStatus, useRecordOfflinePayment } from '../../services/vendorBookingService';
import { useCreateInvoiceFromBooking } from '../../services/vendorInvoiceService';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { paymentApi } from '../../services/paymentService';
import PaymentHistoryModal from '../../components/client/PaymentHistoryModal';
import StatusUpdateModal from '../../components/vendor/StatusUpdateModal';
import EventStartModal from '../../components/vendor/EventStartModal';
import EventEndModal from '../../components/vendor/EventEndModal';
import EventLogs from '../../components/shared/EventLogs';
import { useEventLogs } from '../../hooks/useEventLogs';
import BookingChatPanel from '../../components/booking/BookingChatPanel';
import { bookingChatUnreadQueryKey, fetchBookingChatUnreadCount } from '../../services/bookingChatService';
import { useBookingChatRealtime, CHAT_CONNECTION } from '../../hooks/useBookingChatRealtime';
import { useAuth } from '../../contexts/AuthContext';
import { formatDate, formatTime } from '../../utils/dateUtils';
import { paymentVendorShareDisplay } from '../../utils/formatMoney';
import { useToastContext } from '../../contexts/ToastContext';
import Button from '../../components/shared/Button';
import Input from '../../components/shared/Input';
import ModalShell from '../../components/shared/ModalShell';
import {
 BookingHeader,
 EventDetails,
 ServicePackageDetails,
 ClientReview,
 ClientInformation,
 PaymentSummary
} from '../../components/vendor/booking';
import EventDateChangeReviewPanel from '../../components/vendor/booking/EventDateChangeReviewPanel';
import BookingLinkedInvoices from '../../components/shared/BookingLinkedInvoices';
import BookingLinkedContracts from '../../components/shared/BookingLinkedContracts';
import { VendorGuideCallout } from '../../components/vendor/guidance';
import AskAgentModal from '../../components/vendor/workspace/AskAgentModal';
import { useVendorIntegrations } from '../../services/vendorIntegrationsService';
import { getBookingChannel, CHANNEL, isPrivateBooking as checkPrivateBooking } from '../../utils/bookingSource';

/** Extract HH/MM/SS from "08:00:00", "08:00", or ISO datetime strings. */
const parseClockParts = (value) => {
 if (value == null || value === '') return null;
 const raw = String(value).trim();

 // ISO / datetime: "...T08:00:00" or "... 08:00:00"
 const isoMatch = raw.match(/(?:T|\s)(\d{1,2}):(\d{2})(?::(\d{2}))?/);
 if (isoMatch) {
 return {
 hh: parseInt(isoMatch[1], 10),
 mm: parseInt(isoMatch[2], 10),
 ss: parseInt(isoMatch[3] || '0', 10),
 };
 }

 // Plain time: "08:00:00" or "08:00"
 const timeMatch = raw.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?/);
 if (timeMatch) {
 return {
 hh: parseInt(timeMatch[1], 10),
 mm: parseInt(timeMatch[2], 10),
 ss: parseInt(timeMatch[3] || '0', 10),
 };
 }

 return null;
};

/** True when scheduled event end (date + end_time) is in the past. */
const hasEventSchedulePassed = (booking) => {
 if (!booking?.event_date) return false;

 const dateRaw = String(booking.event_date).trim();
 const dateMatch = dateRaw.match(/(\d{4})-(\d{2})-(\d{2})/);
 if (!dateMatch) {
 const fallback = new Date(booking.event_date);
 if (Number.isNaN(fallback.getTime())) return false;
 // Compare calendar day only if we cannot parse Y-M-D
 const endOfDay = new Date(
 fallback.getFullYear(),
 fallback.getMonth(),
 fallback.getDate(),
 23,
 59,
 59
 );
 return Date.now() > endOfDay.getTime();
 }

 const y = parseInt(dateMatch[1], 10);
 const m = parseInt(dateMatch[2], 10);
 const d = parseInt(dateMatch[3], 10);

 const clock = parseClockParts(booking.end_time) || { hh: 23, mm: 59, ss: 59 };
 const endAt = new Date(y, m - 1, d, clock.hh, clock.mm, clock.ss);
 if (Number.isNaN(endAt.getTime())) return false;

 return Date.now() > endAt.getTime();
};

const VendorBookingDetails = () => {
 const { id } = useParams();
 const navigate = useNavigate();
 const { user } = useAuth();
 const queryClient = useQueryClient();
 const { success, error: showError } = useToastContext();
 const [showStatusModal, setShowStatusModal] = useState(false);
 const [showPaymentHistoryModal, setShowPaymentHistoryModal] = useState(false);
 const [showStartEventModal, setShowStartEventModal] = useState(false);
 const [showEndEventModal, setShowEndEventModal] = useState(false);
 const [startEventSuccess, setStartEventSuccess] = useState(false);
 const [endEventSuccess, setEndEventSuccess] = useState(false);
 const [showBookingChat, setShowBookingChat] = useState(false);
 const [showGenerateReceiptModal, setShowGenerateReceiptModal] = useState(false);
 const [receiptDueDate, setReceiptDueDate] = useState('');
 const [receiptTaxRate, setReceiptTaxRate] = useState('');
 const [showAskAgent, setShowAskAgent] = useState(false);

 // Event logs hook
 const { eventLogs, startEvent, endEvent, isStarting, isEnding } = useEventLogs(id);
 const { data: integRes } = useVendorIntegrations({ enabled: Boolean(id) });
 const canAgents = Boolean(integRes?.data?.capabilities?.can_agents);

 const createReceiptMutation = useCreateInvoiceFromBooking({
 onSuccess: (res) => {
 const invoiceId = res?.data?.id;
 const alreadyExists = Boolean(res?.already_exists);
 success(
 alreadyExists ? 'Receipt found' : 'Receipt created',
 alreadyExists
 ? 'Opening the existing receipt for this order.'
 : 'Your receipt has been created.'
 );
 setShowGenerateReceiptModal(false);
 if (invoiceId) navigate(`/vendor/invoices/${invoiceId}`);
 },
 onError: (err) => {
 showError('Receipt failed', err?.response?.data?.message || 'Could not generate receipt.');
 },
 });

 // Close start event modal when mutation completes successfully
 useEffect(() => {
 if (startEventSuccess && !isStarting) {
 setShowStartEventModal(false);
 setStartEventSuccess(false);
 }
 }, [startEventSuccess, isStarting]);

 // Close end event modal when mutation completes successfully
 useEffect(() => {
 if (endEventSuccess && !isEnding) {
 setShowEndEventModal(false);
 setEndEventSuccess(false);
 }
 }, [endEventSuccess, isEnding]);

 // Fetch booking details
 const { data: bookingResponse, isLoading, error } = useVendorBooking(id);
 const booking = bookingResponse?.data;

 const closeBookingChat = () => {
 setShowBookingChat(false);
 if (id) {
 queryClient.invalidateQueries({ queryKey: bookingChatUnreadQueryKey(id, false) });
 }
 };

 const { connectionStatus: chatConnectionStatus } = useBookingChatRealtime(id, {
 enabled: Boolean(id && booking && !showBookingChat),
 currentUserId: user?.id,
 onMessage: () => {
 queryClient.invalidateQueries({ queryKey: bookingChatUnreadQueryKey(id, false) });
 },
 });

 const { data: chatUnreadResponse } = useQuery({
 queryKey: bookingChatUnreadQueryKey(id, false),
 queryFn: () => fetchBookingChatUnreadCount(id, false),
 enabled: Boolean(id && booking && !showBookingChat),
 refetchInterval:
 showBookingChat || chatConnectionStatus === CHAT_CONNECTION.LIVE ? false : 12000,
 });
 const chatUnreadCount = chatUnreadResponse?.data?.unread_count ?? 0;

 // Fetch payments for this booking
 const { data: paymentsResponse } = useQuery({
 queryKey: ['payments', id],
 queryFn: () => paymentApi.getPaymentByBooking(id),
 enabled: !!id && !!booking,
 select: (response) => response?.data || []
 });

 const payments = paymentsResponse || [];

 // Update booking status mutation
 const updateStatusMutation = useUpdateBookingStatus({
 onSuccess: () => {
 queryClient.invalidateQueries(['vendorBooking', id]);
 setShowStatusModal(false);
 },
 onError: (error) => {
 console.error('Failed to update booking status:', error);
 }
 });

 const recordOfflinePaymentMutation = useRecordOfflinePayment({
 onSuccess: () => {
 success('Payment recorded', 'Offline payment has been recorded for this private booking.');
 },
 onError: (err) => {
 showError(
 'Could not record payment',
 err?.response?.data?.message || 'Please try again.'
 );
 },
 });

 const handleStatusUpdate = (newStatus, reason) => {
 if (newStatus && newStatus !== booking?.status) {
 updateStatusMutation.mutate({
 bookingId: id,
 status: newStatus,
 reason: reason || ''
 });
 }
 };

 // Helper function to safely format currency
 const formatCurrency = (value) => {
 // Handle all edge cases
 if (value === null || value === undefined || value === '' || value === 'null' || value === 'undefined') {
 return '0.00';
 }
 // Convert to string and clean
 const strValue = String(value).trim();
 if (strValue === '' || strValue === 'NaN' || strValue === 'null' || strValue === 'undefined') {
 return '0.00';
 }
 // Remove any non-numeric characters except decimal point and minus sign
 const cleaned = strValue.replace(/[^0-9.-]/g, '');
 if (cleaned === '' || cleaned === '-' || cleaned === '.') {
 return '0.00';
 }
 const num = parseFloat(cleaned);
 if (isNaN(num) || !isFinite(num)) {
 return '0.00';
 }
 return num.toFixed(2);
 };

 const getStatusColor = (status) => {
 const colors = {
 pending: 'bg-yellow-100 text-yellow-800',
 confirmed: 'bg-green-100 text-green-800',
 in_progress: 'bg-surface-muted text-ink',
 completed: 'bg-green-100 text-green-800',
 cancelled: 'bg-red-100 text-red-800',
 no_show: 'bg-gray-100 text-gray-800'
 };
 return colors[status] || colors.pending;
 };

 const formatStatusText = (status) => {
 return status?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Pending';
 };

 // Generate invoice HTML for download
 const generateInvoiceHTML = () => {
 if (!booking) {
 alert('Booking data not available');
 return '';
 }
 
 // Ensure booking has required fields
 if (!booking.booking_reference) {
 alert('Booking reference not available');
 return '';
 }

 const completedPayments = payments.filter(p => p.status === 'completed');
 
 // Helper function to safely parse numeric values (handles strings from database)
 // Laravel decimal:2 casts return as strings in JSON, so we need robust parsing
 const getNumeric = (val) => {
 // Handle null, undefined, empty
 if (val === null || val === undefined || val === '') return 0;
 
 // Convert to string and trim
 const strVal = String(val).trim();
 if (strVal === '' || strVal === 'null' || strVal === 'undefined' || strVal === 'NaN') {
 return 0;
 }
 
 // Remove any non-numeric characters except decimal point and minus sign
 const cleaned = strVal.replace(/[^0-9.-]/g, '');
 if (cleaned === '' || cleaned === '-' || cleaned === '.') {
 return 0;
 }
 
 // Parse to float
 const parsed = parseFloat(cleaned);
 
 // Validate result
 if (isNaN(parsed) || !isFinite(parsed)) {
 console.warn('getNumeric: Invalid value parsed', { original: val, cleaned, parsed });
 return 0;
 }
 
 return parsed;
 };
 
 // For vendors: calculate total service amount in display currency (USD)
 const totalServiceAmount = completedPayments.reduce((sum, p) => {
 const serviceAmount = paymentVendorShareDisplay(p);
 // Ensure we're adding valid numbers
 if (isNaN(serviceAmount) || !isFinite(serviceAmount)) {
 console.warn('Invalid serviceAmount in payment calculation', { payment: p, serviceAmount });
 return sum;
 }
 return sum + serviceAmount;
 }, 0);
 
 // Final validation of totalServiceAmount
 const finalTotalServiceAmount = (isNaN(totalServiceAmount) || !isFinite(totalServiceAmount)) ? 0 : totalServiceAmount;
 
 const client = booking?.client;
 const service = booking?.vendor_service
 || (booking?.custom_service_name ? { service_name: booking.custom_service_name } : null);
 const packageData = booking?.vendor_service_package;
 const addonRows = (booking?.booking_addons || booking?.bookingAddons || []).filter(
 (addon) => addon && (addon.addon_name || addon.name)
 );
 const addonHtml = addonRows.map((addon) => {
 const name = addon.addon_name || addon.name;
 const qty = Math.max(1, Number(addon.quantity) || 1);
 const amount = addon.total_addon_price != null && addon.total_addon_price !== ''
 ? getNumeric(addon.total_addon_price)
 : getNumeric(addon.addon_price) * qty;
 const qtyLabel = qty > 1 ? ` × ${qty}` : '';
 return `<div class="row"><span class="label">Add-on: ${name}${qtyLabel}</span><span class="value">$${Number(amount).toFixed(2)}</span></div>`;
 }).join('');
 
 // Safely get amounts with proper parsing and validation
 // Values come from database as strings (Laravel decimal:2 casts), so we need to parse them properly
 // Use vendor_payout_amount as primary source (what vendor receives)
 // Debug: Log the raw values to see what we're getting
 console.log('Invoice Generation - Raw Booking Data:', {
 'booking.vendor_payout_amount': booking.vendor_payout_amount,
 'booking.subtotal_amount': booking.subtotal_amount,
 'booking.platform_fee': booking.platform_fee,
 'vendor_payout_type': typeof booking.vendor_payout_amount,
 'subtotal_type': typeof booking.subtotal_amount
 });
 
 const vendorPayoutRaw = booking.vendor_payout_amount ?? booking.subtotal_amount ?? 0;
 const subtotalRaw = booking.subtotal_amount ?? booking.vendor_payout_amount ?? 0;
 
 const vendorPayout = getNumeric(vendorPayoutRaw);
 const subtotal = getNumeric(subtotalRaw);
 
 // Ensure we have valid numbers (fallback to 0 if somehow invalid)
 const finalVendorPayout = (isNaN(vendorPayout) || !isFinite(vendorPayout)) ? 0 : vendorPayout;
 const finalSubtotal = (isNaN(subtotal) || !isFinite(subtotal)) ? finalVendorPayout : subtotal;
 
 // Final validation - ensure we never pass NaN to formatCurrency
 if (isNaN(finalVendorPayout) || isNaN(finalSubtotal)) {
 console.error('Invoice Generation: NaN detected after parsing!', {
 vendorPayoutRaw,
 subtotalRaw,
 vendorPayout,
 subtotal,
 finalVendorPayout,
 finalSubtotal
 });
 }
 
 // Helper functions for invoice template
 const formatDateForInvoice = (date) => formatDate(date, 'long');
 const formatCurrencyForInvoice = (value) => {
 // Double-check we're not passing NaN or invalid values
 if (value === null || value === undefined || value === '') {
 return '0.00';
 }
 // If it's already a number, validate it
 if (typeof value === 'number') {
 if (isNaN(value) || !isFinite(value)) {
 console.error('formatCurrencyForInvoice: Received NaN or invalid number', value);
 return '0.00';
 }
 return formatCurrency(value);
 }
 // If it's a string or other type, parse it first
 const numValue = getNumeric(value);
 if (isNaN(numValue) || !isFinite(numValue)) {
 console.error('formatCurrencyForInvoice: Parsed to NaN', { original: value, parsed: numValue });
 return '0.00';
 }
 return formatCurrency(numValue);
 };
 
 return `
 <!DOCTYPE html>
 <html>
 <head>
 <title>Receipt - ${booking.booking_reference}</title>
 <style>
 body { font-family: Arial, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; }
 .header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #000; padding-bottom: 20px; }
 .receipt-title { font-size: 24px; font-weight: bold; margin-bottom: 10px; }
 .booking-ref { font-size: 14px; color: #666; }
 .section { margin-bottom: 25px; }
 .section-title { font-size: 18px; font-weight: bold; margin-bottom: 15px; border-bottom: 1px solid #ddd; padding-bottom: 5px; }
 .row { display: flex; justify-content: space-between; margin-bottom: 8px; }
 .label { font-weight: 600; }
 .value { text-align: right; }
 .payment-item { margin-bottom: 10px; padding: 10px; background: #f5f5f5; border-radius: 4px; }
 .total { font-size: 18px; font-weight: bold; margin-top: 20px; padding-top: 15px; border-top: 2px solid #000; }
 .footer { margin-top: 40px; text-align: center; font-size: 12px; color: #666; }
 @media print { body { padding: 20px; } }
 </style>
 </head>
 <body>
 <div class="header">
 <div class="receipt-title">PAYMENT RECEIPT</div>
 <div class="booking-ref">Booking #${booking.booking_reference}</div>
 <div style="margin-top: 10px; font-size: 12px;">Date: ${formatDateForInvoice(booking.created_at)}</div>
 </div>

 <div class="section">
 <div class="section-title">Client Information</div>
 <div class="row"><span class="label">Name:</span><span class="value">${clientDisplayName}</span></div>
 <div class="row"><span class="label">Phone:</span><span class="value">${client?.phone || 'N/A'}</span></div>
 <div class="row"><span class="label">Email:</span><span class="value">${client?.email || 'N/A'}</span></div>
 </div>

 <div class="section">
 <div class="section-title">Booking Details</div>
 <div class="row"><span class="label">Event Date:</span><span class="value">${formatDateForInvoice(booking.event_date)}</span></div>
 <div class="row"><span class="label">Service:</span><span class="value">${service?.service_name || 'N/A'}</span></div>
 ${packageData ? `<div class="row"><span class="label">Package:</span><span class="value">${packageData.package_name}</span></div>` : ''}
 ${addonHtml}
 </div>

 <div class="section">
 <div class="section-title">Payment Summary</div>
 <div class="row"><span class="label">Subtotal:</span><span class="value">$${formatCurrencyForInvoice(finalSubtotal)}</span></div>
 <div class="row"><span class="label">Vendor Payout:</span><span class="value">$${formatCurrencyForInvoice(finalVendorPayout)}</span></div>
 </div>

 <div class="section">
 <div class="section-title">Payment Transactions</div>
 ${completedPayments.length > 0 ? completedPayments.map(payment => {
 const serviceAmount = Math.max(0, paymentVendorShareDisplay(payment));
 
 return `
 <div class="payment-item">
 <div class="row"><span class="label">Payment Reference:</span><span class="value">${payment.payment_reference}</span></div>
 <div class="row"><span class="label">Type:</span><span class="value">${payment.payment_type === 'deposit' ? 'Deposit' : 'Full Payment'}</span></div>
 <div class="row"><span class="label">Service Amount:</span><span class="value">$${formatCurrencyForInvoice(serviceAmount)}</span></div>
 <div class="row"><span class="label">Date:</span><span class="value">${formatDateForInvoice(payment.created_at)}</span></div>
 </div>
 `;
 }).join('') : '<div class="payment-item">No payments recorded</div>'}
 </div>

 <div class="total">
 <div class="row">
 <span class="label">Total Paid:</span>
 <span class="value">$${formatCurrencyForInvoice(finalTotalServiceAmount)}</span>
 </div>
 </div>

 <div class="footer">
 <p>Thank you for your business!</p>
 <p>This is an official receipt for your records.</p>
 </div>
 </body>
 </html>
 `;
 };

 const handleDownloadInvoice = () => {
 try {
 const invoiceHTML = generateInvoiceHTML();
 if (!invoiceHTML || invoiceHTML === '') {
 return; // Error already handled in generateInvoiceHTML
 }
 const printWindow = window.open('', '_blank');
 if (!printWindow) {
 alert('Please allow pop-ups to download the invoice');
 return;
 }
 printWindow.document.write(invoiceHTML);
 printWindow.document.close();
 setTimeout(() => {
 printWindow.print();
 }, 250);
 } catch (error) {
 console.error('Error generating invoice:', error);
 alert('Failed to generate invoice. Please try again later.');
 }
 };

 const openGenerateReceiptModal = () => {
 const due = new Date();
 due.setDate(due.getDate() + 14);
 setReceiptDueDate(due.toISOString().slice(0, 10));
 setReceiptTaxRate('');
 setShowGenerateReceiptModal(true);
 };

 const submitGenerateReceipt = () => {
 if (!id) return;
 createReceiptMutation.mutate({
 bookingId: id,
 payload: {
 due_date: receiptDueDate,
 tax_rate: receiptTaxRate ? Number(receiptTaxRate) : 0,
 status: 'draft',
 document_type: 'receipt',
 },
 });
 };

 // Skeleton Loading Component
 const SkeletonLoader = () => (
 <div className="space-y-5 animate-pulse">
 <div className="h-5 w-32 bg-gray-200 rounded"></div>
 <div className="bg-surface border border-black rounded-lg p-2 sm:p-3">
 <div className="h-6 w-48 bg-gray-200 rounded mb-2"></div>
 </div>
 <div className="grid grid-cols-1 lg:grid-cols-3 gap-2 sm:gap-3">
 <div className="lg:col-span-2 space-y-2 sm:space-y-3">
 <div className="bg-surface border border-black rounded-lg p-2 sm:p-3">
 <div className="h-5 w-32 bg-gray-200 rounded mb-3"></div>
 <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
 {[1, 2, 3, 4].map((i) => (
 <div key={i} className="h-16 bg-gray-200 rounded"></div>
 ))}
 </div>
 </div>
 </div>
 <div className="space-y-2 sm:space-y-3">
 <div className="bg-surface border border-black rounded-lg p-2 sm:p-3">
 <div className="h-5 w-40 bg-gray-200 rounded mb-3"></div>
 <div className="space-y-2">
 {[1, 2, 3].map((i) => (
 <div key={i} className="h-4 w-full bg-gray-200 rounded"></div>
 ))}
 </div>
 </div>
 </div>
 </div>
 </div>
 );

 if (isLoading) {
 return <SkeletonLoader />;
 }

 if (error || !booking) {
 return (
 <div className="space-y-5">
 <div className="bg-surface border border-black rounded-lg p-6 sm:p-8">
 <div className="text-center">
 <AlertCircle className="w-12 h-12 text-black mx-auto mb-4" />
 <h3 className="text-xl font-bold text-black mb-2">Booking Not Found</h3>
 <p className="text-gray-600 mb-6">
 {error?.response?.data?.message || 'The booking you are looking for does not exist.'}
 </p>
 <button
 onClick={() => navigate('/vendor/bookings')}
 className="inline-flex items-center px-4 py-2 bg-black text-white rounded-md hover:bg-gray-900 transition-colors"
 >
 <ArrowLeft className="w-4 h-4 mr-2" />
 Back to Bookings
 </button>
 </div>
 </div>
 </div>
 );
 }

 const client = booking.client;
 const clientDisplayName =
 booking.client_display_name || booking.guest_name || client?.name || 'Guest client';
 const bookingChannel = getBookingChannel(booking);
 const isPrivateBooking = checkPrivateBooking(booking) || bookingChannel === CHANNEL.EXTERNAL;
 const isExternal = bookingChannel === CHANNEL.EXTERNAL;
 const service = booking.vendor_service
 || (booking.custom_service_name ? { service_name: booking.custom_service_name } : null);
 const packageData = booking.vendor_service_package;

 // Calculate payment totals (display/USD — never Stripe settlement CAD)
 const completedPayments = payments.filter(p => p.status === 'completed');
 const totalServiceAmount = completedPayments.reduce((sum, p) => {
 return sum + paymentVendorShareDisplay(p);
 }, 0);
 const depositPaid = completedPayments.filter(p => p.payment_type === 'deposit')
 .reduce((sum, p) => sum + paymentVendorShareDisplay(p), 0);
 const fullPaymentPaid = completedPayments.filter(p => p.payment_type === 'full_payment').length > 0;
 const discountAmount = parseFloat(booking.discount_amount) || 0;
 const storedPayout = parseFloat(booking.vendor_payout_amount);
 const storedSubtotal = parseFloat(booking.subtotal_amount);
 let vendorPayout = Number.isFinite(storedPayout)
 ? storedPayout
 : Number.isFinite(storedSubtotal)
 ? storedSubtotal
 : 0;
 // Private: treat payout as net. If discount exists but payout still equals subtotal, subtract it.
 if (
 isPrivateBooking &&
 discountAmount > 0 &&
 Number.isFinite(storedSubtotal) &&
 Number.isFinite(storedPayout) &&
 Math.abs(storedSubtotal - storedPayout) < 0.021
 ) {
 vendorPayout = Math.max(0, Math.round((storedSubtotal - discountAmount) * 100) / 100);
 } else if (
 isPrivateBooking &&
 discountAmount > 0 &&
 Number.isFinite(storedSubtotal) &&
 Number.isFinite(storedPayout) &&
 Math.abs(storedSubtotal - discountAmount - storedPayout) < 0.021
 ) {
 vendorPayout = storedPayout;
 }
 const remainingBalance = Math.max(0, vendorPayout - totalServiceAmount);

 const handleMarkOfflinePaid = ({ amount, notes } = {}) => {
 recordOfflinePaymentMutation.mutate({
 bookingId: id,
 amount,
 notes,
 });
 };

 // Check for reviews
 const hasReview = booking.reviews && booking.reviews.length > 0;
 const review = hasReview ? booking.reviews[0] : null;

 const hasStartLog = Boolean(eventLogs.find((log) => log.event_type === 'start'));
 const hasEndLog = Boolean(eventLogs.find((log) => log.event_type === 'end'));
 const eventPassed = hasEventSchedulePassed(booking);
 const canManageEvent =
 booking.status !== 'cancelled' && booking.status !== 'completed' && !hasEndLog;
 // External: complete with End event only — no screenshot start flow.
 const showStartButton =
 !isExternal && canManageEvent && !hasStartLog && !eventPassed;
 const showEndButton =
 canManageEvent && (isExternal || hasStartLog || eventPassed);

 return (
 <div
 className={`space-y-5 ${
 isExternal ? 'rounded-xl border border-teal-200/70 bg-teal-50/20 p-3 sm:p-4' : ''
 }`}
 >
 <BookingHeader
 onBack={() =>
 navigate(isExternal ? '/vendor/private-bookings' : '/vendor/bookings')
 }
 backLabel={isExternal ? 'Back to private bookings' : 'Back to bookings'}
 status={booking.status}
 statusColor={getStatusColor(booking.status)}
 statusText={formatStatusText(booking.status)}
 bookingReference={booking.booking_reference}
 createdAt={booking.created_at}
 formatDate={formatDate}
 channel={bookingChannel}
 clientLabel={clientDisplayName}
 onUpdateStatus={() => {
 if (booking.status !== 'completed') {
 setShowStatusModal(true);
 }
 }}
 />

 <VendorGuideCallout
 title={isExternal ? 'External booking' : 'Internal booking'}
 steps={
 isExternal
 ? [
 'Work this job outside marketplace payouts — guest contact is on the right.',
 'Record full or partial offline payments, invoices, and contracts as needed.',
 'When the job is done, use End event to complete it — no screenshot upload required.',
 ]
 : [
 'Update status as the marketplace event progresses.',
 'Message the client or start/end the event from here.',
 'Earnings and platform fees show in payment summary and accounting.',
 ]
 }
 icon={ListChecks}
 />

 <div className="flex flex-wrap gap-2">
 <Button
 type="button"
 size="sm"
 variant="outline"
 icon={Bot}
 className="!rounded-md"
 onClick={() => setShowAskAgent(true)}
 >
 Run agent
 </Button>
 </div>

 {/* Event Start/End Actions */}
 {booking.status !== 'cancelled' && (
 <div
 className={`rounded-lg border p-5 sm:p-6 shadow-sm ${ eventPassed && canManageEvent ? 'border-amber-200 bg-amber-50/70' : 'border-gray-200 bg-surface' }`}
 >
 <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
 <div className="min-w-0 flex-1">
 <div className="flex flex-wrap items-center gap-2 mb-1.5">
 <h3 className="text-base font-bold text-gray-900">Event management</h3>
 {eventPassed && canManageEvent && (
 <span className="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-amber-800">
 Date passed
 </span>
 )}
 {hasEndLog && (
 <span className="inline-flex items-center rounded-full bg-emerald-100 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-emerald-800">
 Completed
 </span>
 )}
 {hasStartLog && !hasEndLog && (
 <span className="inline-flex items-center rounded-full bg-surface-muted px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-ink">
 In progress
 </span>
 )}
 </div>
 <p className="text-sm text-gray-600 max-w-2xl leading-relaxed">
 {isExternal
 ? hasEndLog
 ? 'This external job is complete.'
 : 'When you finish the job, end the event to mark this booking complete. No screenshots needed.'
 : eventPassed && canManageEvent
 ? 'This event’s scheduled time is over. End it now to mark the booking complete and update your timeline.'
 : hasEndLog
 ? 'This event has been ended and the booking is complete.'
 : 'Start when you begin on site, then end when you finish. That keeps status and history accurate.'}
 </p>
 </div>
 <div className="flex shrink-0 flex-wrap items-center gap-2.5">
 {showStartButton && (
 <button
 type="button"
 onClick={() => setShowStartEventModal(true)}
 className="inline-flex items-center gap-2 px-4 py-2.5 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 transition-colors text-sm font-semibold whitespace-nowrap shadow-sm"
 >
 <Play className="w-4 h-4" />
 Start event
 </button>
 )}
 {showEndButton && (
 <button
 type="button"
 onClick={() => setShowEndEventModal(true)}
 disabled={isEnding}
 className={`inline-flex items-center gap-2 px-4 py-2.5 text-white rounded-xl transition-colors text-sm font-semibold whitespace-nowrap shadow-sm disabled:opacity-50 disabled:cursor-not-allowed ${ eventPassed ? 'bg-gray-900 hover:bg-black' : 'bg-red-600 hover:bg-red-700' }`}
 >
 <Square className="w-4 h-4" />
 {eventPassed && !hasStartLog ? 'End event now' : 'End event'}
 </button>
 )}
 </div>
 </div>
 </div>
 )}

 {/* Event Logs */}
 {eventLogs && eventLogs.length > 0 && (
 <EventLogs eventLogs={eventLogs} isLoading={false} />
 )}

 {(booking.active_event_date_change_request ||
 booking.activeEventDateChangeRequest) && (
 <EventDateChangeReviewPanel
 bookingId={booking.id}
 changeRequest={
 booking.active_event_date_change_request ?? booking.activeEventDateChangeRequest
 }
 clientName={clientDisplayName}
 onResolved={() => {
 queryClient.invalidateQueries({ queryKey: ['vendorBooking', id] });
 }}
 />
 )}

 <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
 <div className="lg:col-span-2 space-y-5">
 <EventDetails
 booking={booking}
 formatDate={formatDate}
 formatTime={formatTime}
 />

 <ServicePackageDetails
 service={service}
 packageData={packageData}
 addons={booking.booking_addons || booking.bookingAddons || []}
 />

 <BookingLinkedContracts
 contracts={booking.contracts || []}
 variant="vendor"
 createHref={
 booking?.id
 ? `/vendor/contracts/create?booking_id=${encodeURIComponent(booking.id)}`
 : null
 }
 />

 <BookingLinkedInvoices
 invoices={booking.invoices || []}
 variant="vendor"
 createHref={
 booking?.id
 ? `/vendor/invoices/create?channel=${isExternal ? 'external' : 'internal'}&booking_id=${encodeURIComponent(booking.id)}`
 : null
 }
 />

 <ClientReview review={review} />
 </div>

 <div className="space-y-5">
 <ClientInformation
 client={
 isPrivateBooking
 ? {
 name: clientDisplayName,
 phone: booking.guest_phone || client?.phone,
 email: booking.guest_email || client?.email,
 }
 : client
 ? { ...client, name: clientDisplayName }
 : { name: clientDisplayName }
 }
 onMessageClient={() => setShowBookingChat(true)}
 unreadChatCount={chatUnreadCount}
 />

 <PaymentSummary
 booking={booking}
 totalPaid={totalServiceAmount}
 depositPaid={depositPaid}
 fullPaymentPaid={fullPaymentPaid}
 payments={payments}
 remainingBalance={remainingBalance}
 isPrivateBooking={isPrivateBooking}
 onMarkOfflinePaid={handleMarkOfflinePaid}
 isMarkingOfflinePaid={recordOfflinePaymentMutation.isPending}
 onDownloadInvoice={handleDownloadInvoice}
 onGenerateReceipt={openGenerateReceiptModal}
 isGeneratingReceipt={createReceiptMutation.isPending}
 onViewPaymentHistory={() => setShowPaymentHistoryModal(true)}
 />
 </div>
 </div>

 {/* Status Update Modal */}
 <StatusUpdateModal
 isOpen={showStatusModal}
 onClose={() => setShowStatusModal(false)}
 currentStatus={booking?.status}
 onUpdate={handleStatusUpdate}
 isUpdating={updateStatusMutation.isPending}
 />

 {/* Payment History Modal */}
 <PaymentHistoryModal
 isOpen={showPaymentHistoryModal}
 onClose={() => setShowPaymentHistoryModal(false)}
 payments={payments}
 isVendorView={true}
 />

 {/* Event Start Modal (internal marketplace only) */}
 <EventStartModal
 isOpen={showStartEventModal}
 onClose={() => {
 setShowStartEventModal(false);
 }}
 onStart={(data) => {
 startEvent(data, {
 onSuccess: () => {
 setStartEventSuccess(true);
 },
 });
 }}
 isLoading={isStarting}
 requireScreenshots={!isExternal}
 />

 {/* Event End Modal */}
 <EventEndModal
 isOpen={showEndEventModal}
 onClose={() => setShowEndEventModal(false)}
 eventPassed={eventPassed}
 willAutoStart={!hasStartLog}
 onEnd={(data) => {
 endEvent(data, {
 onSuccess: () => {
 setEndEventSuccess(true);
 },
 });
 }}
 isLoading={isEnding}
 />

 <ModalShell
 isOpen={showGenerateReceiptModal}
 onClose={() => !createReceiptMutation.isPending && setShowGenerateReceiptModal(false)}
 labelledBy="generate-receipt-title"
 panelClassName="max-w-md"
 >
 <div className="bg-surface rounded-lg shadow-sm border border-border p-6">
 <div className="flex items-center justify-between mb-4">
 <h3 id="generate-receipt-title" className="text-lg font-semibold text-ink">
 Generate receipt
 </h3>
 <button
 type="button"
 onClick={() => setShowGenerateReceiptModal(false)}
 className="text-gray-400 hover:text-gray-600"
 aria-label="Close"
 >
 <X className="w-5 h-5" />
 </button>
 </div>
 <p className="text-sm text-ink-secondary mb-4">
 Receipt for <strong className="text-ink">{clientDisplayName}</strong>
 {(booking?.vendor_payout_amount ?? booking?.subtotal_amount) != null && (
 <> — ${Number(booking.vendor_payout_amount ?? booking.subtotal_amount).toFixed(2)}</>
 )}
 </p>
 <div className="space-y-4">
 <Input
 label="Due date"
 type="date"
 value={receiptDueDate}
 onChange={(e) => setReceiptDueDate(e.target.value)}
 />
 <Input
 label="Tax rate (%)"
 type="number"
 min="0"
 max="100"
 step="0.01"
 value={receiptTaxRate}
 onChange={(e) => setReceiptTaxRate(e.target.value)}
 placeholder="0"
 />
 </div>
 <div className="flex gap-2 mt-6 justify-end">
 <Button variant="outline" onClick={() => setShowGenerateReceiptModal(false)}>
 Cancel
 </Button>
 <Button onClick={submitGenerateReceipt} loading={createReceiptMutation.isPending}>
 Create receipt
 </Button>
 </div>
 </div>
 </ModalShell>

 <ModalShell
 isOpen={Boolean(showBookingChat && id)}
 onClose={closeBookingChat}
 className="items-end sm:items-center"
 panelClassName="max-w-lg"
 >
 <BookingChatPanel bookingId={id} onClose={closeBookingChat} className="max-h-[90vh]" />
 </ModalShell>

 <AskAgentModal
 isOpen={showAskAgent}
 onClose={() => setShowAskAgent(false)}
 canAgents={canAgents}
 presetEntity={
 booking
 ? {
 type: 'booking',
 id: String(booking.id),
 label:
 booking.booking_reference ||
 clientDisplayName ||
 'Booking',
 }
 : null
 }
 />
 </div>
 );
};

export default VendorBookingDetails;
